import { Group, Stack, TextInput } from "@mantine/core"
import { IconSearch } from "@tabler/icons-react"
import { PaginationState, RowSelectionState } from "@tanstack/react-table"
import { ColumnManager, Loader, Select } from "@tfg/ui"
import { Dispatch, SetStateAction, useState } from "react"
import { useTranslation } from "react-i18next"

import { FindProductLcasResponse } from "@tfg/types/schemas/modules/lifeCycleAnalysis/dtos/find-product-lcas.response.dto"
import { fetchDataOptionsValidationType } from "@tfg/types/schemas/products"

import { useCustomReactTable } from "~/components/hooks/useCustomReactTable"
import { CustomTable } from "~/components/table/CustomTable"
import {
  TABLE_CONTEXT_MAP,
  ViewHeader,
} from "~/components/table/views/ViewHeader"
import { Views } from "~/components/table/views/Views"
import { useGetColumnManagerTranslations } from "~/hooks/table"
import { RunLcaOnProductsButton } from "~/pages/Brand/Regulations/LCA/RunLcaOnProductsButton.tsx"
import { ECOBALYSE_ANALYSIS_STATUS_TRANSLATIONS } from "~/pages/Brand/Regulations/LCA/ecobalyseAnalysisStatusTranslations"
import { useLcaColumns } from "~/pages/Brand/Regulations/LCA/useLCAColumns.tsx"

interface Props {
  data: FindProductLcasResponse
  fetchDataOptions: fetchDataOptionsValidationType
  setPagination: Dispatch<SetStateAction<PaginationState>>
  isFetching: boolean
  searchProduct: string | undefined
  setSearchProduct: (
    value: ((prevState: string | undefined) => string) | string
  ) => void
  statusFilter: string | null
  setStatusFilter: Dispatch<SetStateAction<string | null>>
}

export const ProductsLCATable = ({
  data,
  fetchDataOptions,
  setPagination,
  isFetching,
  setSearchProduct,
  searchProduct,
  statusFilter,
  setStatusFilter,
}: Props) => {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const columns = useLcaColumns()
  const { t } = useTranslation("regulations")
  const [isColumnManagerOpen, setIsColumnManagerOpen] = useState<boolean>(false)
  const columnManagerTranslations = useGetColumnManagerTranslations()
  const table = useCustomReactTable({
    state: {
      pagination: {
        pageIndex: fetchDataOptions.skip / fetchDataOptions.take,
        pageSize: fetchDataOptions.take,
      },
      rowSelection,
    },
    initialColumnPinning: { left: ["name"] },
    manualPagination: true,
    data: data.data,
    pageCount: Math.ceil(data.total / fetchDataOptions.take),
    columns,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: setPagination,

    getRowId: (row) => String(row.id),
  })

  return (
    <Stack>
      <Views tableContext={TABLE_CONTEXT_MAP.LCA} />
      <Group justify="space-between">
        <Group>
          <TextInput
            w={180}
            autoFocus
            placeholder={t("lists.placeholders.search")}
            leftSection={<IconSearch size={16} />}
            value={searchProduct}
            onChange={(event) => setSearchProduct(event.currentTarget.value)}
            rightSection={isFetching && <Loader size={16} />}
          />
          <Select
            data={Object.entries(ECOBALYSE_ANALYSIS_STATUS_TRANSLATIONS).map(
              ([key, translation]) => ({
                value: key,
                label: translation,
              })
            )}
            value={statusFilter}
            clearable
            onChange={(value) => setStatusFilter(value)}
          />
          <ColumnManager
            table={table}
            translations={columnManagerTranslations}
            isOpen={isColumnManagerOpen}
            setIsOpen={setIsColumnManagerOpen}
          />
          <ViewHeader tableContext={TABLE_CONTEXT_MAP.LCA} />
        </Group>
        <Group>
          <RunLcaOnProductsButton
            params={{
              productIds: Object.keys(rowSelection).map(Number),
            }}
          />
        </Group>
      </Group>
      <CustomTable
        table={table}
        pagination={true}
        manualPaginationCount={data.total}
        setOpenColumnManager={setIsColumnManagerOpen}
      />
    </Stack>
  )
}
